  window.VocalShopKits = window.VocalShopKits || {};
  (function () {
    const DEBUG = false;

    function log(...args) {
      if (DEBUG) console.log("[VocalShop Kit Engine]", ...args);
    }

    function getCurrentKitConfig() {
      return (
        window.VOCALSHOP_CURRENT_KIT ||
        window.VOCALSHOP_KIT_CONFIG ||
        null
      );
    }

    function normalizeComponent(component) {
      return {
        handle: component.handle,
        variantId: Number(
          component.variantId ||
          component.defaultVariantId ||
          component.id
        ),
        quantity: Number(component.quantity || 1),
        subscription: Boolean(component.subscription),
        defaultSellingPlanId: component.defaultSellingPlanId
          ? Number(component.defaultSellingPlanId)
          : null,
        properties: component.properties || {}
      };
    }

    async function fetchProduct(handle) {
      const response = await fetch(`/products/${handle}.js`, {
        credentials: "same-origin",
        headers: {
          "Accept": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Could not fetch product: ${handle}`);
      }

      return response.json();
    }

    function findVariant(product, variantId) {
      const variant = product.variants.find(function (variant) {
        return Number(variant.id) === Number(variantId);
      });

      if (!variant) {
        throw new Error(
          `Variant ${variantId} not found on product ${product.handle}`
        );
      }

      return variant;
    }

    function resolveSellingPlan(component, variant, subscribe, sellingPlanOverride) {
      if (!subscribe || !component.subscription) return null;

      const allocations = variant.selling_plan_allocations || [];

      if (!allocations.length) {
        log("No selling plan allocations for variant", variant.id);
        return null;
      }

      // Cadence override (VocalMist PDP frequency selector, 2026-07-06): honor a
      // customer-chosen selling plan ONLY when this variant actually carries it.
      // Preserves the engine's safety invariant — we never attach a plan the
      // variant doesn't own; if the override plan is absent we fall through to
      // the component's default below (never a Shopify rejection).
      if (sellingPlanOverride) {
        const overrideAllocation = allocations.find(function (allocation) {
          return (
            Number(allocation.selling_plan_id) === Number(sellingPlanOverride)
          );
        });

        if (overrideAllocation) {
          return Number(overrideAllocation.selling_plan_id);
        }

        log("Override plan not on variant, falling back to default", {
          variant: variant.id,
          sellingPlanOverride
        });
      }

      if (component.defaultSellingPlanId) {
        const matchingAllocation = allocations.find(function (allocation) {
          return (
            Number(allocation.selling_plan_id) ===
            Number(component.defaultSellingPlanId)
          );
        });

        if (matchingAllocation) {
          return Number(matchingAllocation.selling_plan_id);
        }
      }

      return Number(allocations[0].selling_plan_id);
    }

async function buildCartItem(component, options) {
  const normalized = normalizeComponent(component);
  const product = await fetchProduct(normalized.handle);
  const variant = findVariant(product, normalized.variantId);

  if (!variant.available) {
    throw new Error(`${product.title} is currently out of stock`);
  }

  const sellingPlanId = resolveSellingPlan(
    normalized,
    variant,
    options.subscribe,
    options.sellingPlanOverride
  );

  const item = {
    id: normalized.variantId,
    quantity: normalized.quantity
  };

  if (sellingPlanId) {
    item.selling_plan = sellingPlanId;
  }

  const properties = Object.assign({}, normalized.properties, options.kitProperties || {});

  if (Object.keys(properties).length) {
    item.properties = properties;
  }

  return item;
}

    async function addItemsToCart(items) {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ items: items })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Could not add kit to cart");
      }

      return response.json();
    }

    function getDiscountCode(kitConfig, subscribe) {
      if (!kitConfig) return null;

      if (subscribe) {
        return (
          kitConfig.subscriptionDiscountCode ||
          kitConfig.kitSubscriptionDiscountCode ||
          kitConfig.discounts?.subscription ||
          null
        );
      }

      return (
        kitConfig.discountCode ||
        kitConfig.kitDiscountCode ||
        kitConfig.discounts?.oneTime ||
        null
      );
    }

    async function fetchCart() {
      const response = await fetch("/cart.js", {
        credentials: "same-origin",
        headers: { "Accept": "application/json" }
      });

      if (!response.ok) {
        throw new Error("Could not read cart");
      }

      return response.json();
    }

    // Apply the kit discount via the AJAX Cart API (POST /cart/update.js with
    // `discount`) instead of the legacy /discount/CODE cookie redirect. The
    // update call persists codes on the cart itself (no session-only cookie),
    // keeps the customer in-page, and stacks with codes already on the cart
    // (e.g. NATS26) by re-submitting the merged list.
    async function applyKitDiscount(discountCode) {
      const cart = await fetchCart();

      const existingCodes = (cart.discount_codes || [])
        .map(function (entry) { return entry && entry.code; })
        .filter(Boolean);

      // Metafield may carry multiple comma-separated codes (e.g. a kit code
      // plus the free-first-refill code on subscribe adds).
      const requested = String(discountCode).split(",")
        .map(function (s) { return s.trim(); })
        .filter(Boolean);

      const codes = existingCodes.slice();
      requested.forEach(function (rc) {
        const present = codes.some(function (code) {
          return code.toLowerCase() === rc.toLowerCase();
        });
        if (!present) codes.push(rc);
      });

      const response = await fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ discount: codes.join(",") })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Could not apply kit discount");
      }

      const updatedCart = await response.json();

      const applied = requested.every(function (rc) {
        return (updatedCart.discount_codes || []).some(function (entry) {
          return (
            entry &&
            entry.code &&
            entry.code.toLowerCase() === rc.toLowerCase() &&
            entry.applicable !== false
          );
        });
      });

      return { applied: applied, cart: updatedCart };
    }

    // Verify the discount landed; if the AJAX path failed or is inconclusive,
    // fall back to the legacy /discount/ URL so the customer still gets the
    // promised price. Never leaves the customer worse off than the old flow.
    async function finishAdd(kitConfig, subscribe, redirect) {
      const discountCode = getDiscountCode(kitConfig, subscribe);

      if (!discountCode) {
        if (redirect) window.location.href = "/cart";
        return { discountApplied: null };
      }

      try {
        const result = await applyKitDiscount(discountCode);

        if (result.applied) {
          if (redirect) window.location.href = "/cart";
          return { discountApplied: true };
        }

        log("AJAX discount inconclusive, using legacy redirect", discountCode);
      } catch (error) {
        log("AJAX discount failed, using legacy redirect", error);
      }

      if (redirect) {
        const firstCode = String(discountCode).split(",")[0].trim();
        window.location.href =
          `/discount/${encodeURIComponent(firstCode)}?redirect=/cart`;
      }

      return { discountApplied: false };
    }

    async function addKit(options) {
      const opts = options || {};
      const kitConfig = opts.kitConfig || getCurrentKitConfig();

      if (!kitConfig) {
        throw new Error("No VocalShop kit config found on this page");
      }

      const components =
        kitConfig.components ||
        kitConfig.kitComponents ||
        kitConfig.items ||
        [];

      if (!Array.isArray(components) || !components.length) {
        throw new Error("No kit components found");
      }

      const subscribe = Boolean(opts.subscribe);

      // Optional cadence override: a customer-selected selling plan applied to
      // every subscribable component on this add (VocalMist PDP frequency
      // selector). Only meaningful on the subscribe path; ignored otherwise and
      // ignored per-component when the variant doesn't carry the plan.
      const sellingPlanOverride = subscribe && opts.sellingPlanOverride
        ? Number(opts.sellingPlanOverride)
        : null;

      // Hidden (underscore-prefixed) line properties: group the components as
      // one kit in cart rendering, and give discount/validation Functions a
      // stable key to target. Not shown to customers.
      const kitProperties = {
        _kit_id: kitConfig.productHandle || String(kitConfig.productId || ""),
        _kit_name: kitConfig.title || "",
        _kit_size: String(components.length)
      };

      log("Adding kit", { subscribe, components });

      const items = await Promise.all(
        components.map(function (component) {
          return buildCartItem(component, {
            subscribe: subscribe,
            sellingPlanOverride: sellingPlanOverride,
            kitProperties: kitProperties
          });
        })
      );

      // Kit marker (F3, 2026-07-26): $0 non-shipping signature line — AfterSell's
      // kit funnel triggers on this product instead of order discount percent.
      // Same-variant adds merge into one line; quantity is irrelevant to the trigger.
      items.push({
        id: 46442961043654,
        quantity: 1,
        properties: Object.assign({ _kit_marker: "1" }, kitProperties)
      });

      await addItemsToCart(items);

      const redirect = opts.redirect !== false;
      const discountResult = await finishAdd(kitConfig, subscribe, redirect);

      return { ok: true, items: items, discountApplied: discountResult.discountApplied };
    }

    function setButtonLoading(button, loading) {
      if (!button) return;

      if (loading) {
        button.dataset.originalText = button.textContent;
        button.disabled = true;
        button.textContent = "Adding...";
      } else {
        button.disabled = false;
        if (button.dataset.originalText) {
          button.textContent = button.dataset.originalText;
        }
      }
    }

    async function handleButtonClick(event) {
      const button = event.target.closest("[data-vocalshop-kit-action]");
      if (!button) return;

      event.preventDefault();

      const action = button.dataset.vocalshopKitAction;
      const subscribe = action === "subscribe";

      // Read the cadence selector (present only on kits that render one — the
      // VocalMist PDP today). Absent select ⇒ null ⇒ default behavior unchanged.
      let sellingPlanOverride = null;
      if (subscribe) {
        const root = button.closest("[data-vs-kit-cta]");
        const cadenceSelect = root && root.querySelector("[data-vs-kit-cadence]");
        if (cadenceSelect && cadenceSelect.value) {
          sellingPlanOverride = Number(cadenceSelect.value);
        }
      }

      try {
        setButtonLoading(button, true);
        await addKit({ subscribe: subscribe, sellingPlanOverride: sellingPlanOverride });
      } catch (error) {
        console.error("[VocalShop Kit Engine]", error);
        setButtonLoading(button, false);
        alert("Sorry — something went wrong adding this kit to cart.");
      }
    }

    document.addEventListener("click", handleButtonClick);

    window.VocalShopKits.addKit = addKit;
    window.VocalShopKits.fetchProduct = fetchProduct;
    window.VocalShopKits.buildCartItem = buildCartItem;
    window.VocalShopKits.applyKitDiscount = applyKitDiscount;
    window.VocalShopKits.version = "2026-07-26-kitmarker";
  })();
// VocalShop free-refill cart reconciler — Phase B1, STAGED (do NOT push before
// Phase A cutover; spec: business/vocalmist/free-refill-scaling-spec-2026-07-26.md).
// Append to assets/vocalshop-kit-cart.js (inside its own IIFE) at push time.
//
// Purpose: device adds that bypass the kit engine (collection quick-add, search,
// Buy Button, cart permalinks) get the free Hydration Formula pack auto-added,
// so the Function's entitlement (device units ≥ formula units) actually lands
// in the cart. Engine paths already add the pack as a kit component.
//
// Behavior contract:
// - If device units > formula units in the cart, add the shortfall as ONE-TIME
//   formula lines (customer can still choose subscription via PDP/cadence paths;
//   the Function frees it either way; free unit rides subscription lines first).
// - Runs at most once per (cart token, device count) — a customer who deletes
//   the free pack is not fought. sessionStorage key encodes both.
// - Never touches quantity of existing lines, never removes anything, silent
//   no-op on any error (the Function is the enforcement layer; this is UX).
(function () {
  var DEVICE_VARIANTS = [45071930687686]; // KN-8ZN3-CGIE — keep in sync with kit-price-sync FREE_REFILL_DEVICE_VARIANTS
  var FORMULA_VARIANT = 45071937110214;   // HF Refill Pack of 48
  var STATE_KEY = "vs_refill_reconciled";

  // Engine-managed lines (kit adds) carry _kit_id and already ship with their
  // formula companion in the same /cart/add.js call — exclude them entirely so
  // this reconciler can never race the engine into a double-add (adversarial
  // review 2026-07-26, P2). Only engine-untouched device lines create shortfall.
  function countUnits(cart, variantIds) {
    return cart.items.reduce(function (sum, item) {
      if (item.properties && item.properties._kit_id) return sum;
      return variantIds.indexOf(Number(item.variant_id)) !== -1 ? sum + item.quantity : sum;
    }, 0);
  }

  async function reconcile() {
    try {
      var resp = await fetch("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } });
      if (!resp.ok) return;
      var cart = await resp.json();

      var deviceUnits = countUnits(cart, DEVICE_VARIANTS);
      if (!deviceUnits) return;
      var formulaUnits = countUnits(cart, [FORMULA_VARIANT]);
      var shortfall = deviceUnits - formulaUnits;
      if (shortfall < 1) return;

      var marker = cart.token + ":" + deviceUnits;
      if (sessionStorage.getItem(STATE_KEY) === marker) return; // already reconciled this state (respects removals)
      sessionStorage.setItem(STATE_KEY, marker);

      var add = await fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          items: [{ id: FORMULA_VARIANT, quantity: shortfall, properties: { _free_refill_auto: "1" } }],
        }),
      });
      if (!add.ok) {
        // Transient failure must not poison the marker — clear it so the next
        // trigger retries (adversarial review 2026-07-26, P2).
        sessionStorage.removeItem(STATE_KEY);
        return;
      }
      if (add.ok) {
        // Re-render whatever cart UI is present; theme-safe fallback = reload on cart page.
        if (document.querySelector("cart-drawer, [data-cart-drawer]") && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        } else if (location.pathname === "/cart") {
          location.reload();
        }
      }
    } catch (e) {
      /* silent — enforcement lives in the Function */
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", reconcile);
  } else {
    reconcile();
  }
  document.addEventListener("cart:item-added", reconcile); // Broadcast theme hook, if emitted
})();
